import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Empty } from "@/components/Empty";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { useDateFormat } from "@/hooks/useDateFormat";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { DeleteAccountModal } from "@/pages/applications/pages/googleAdsIntegration/DeleteAccountModal";
import { EditConversionSettingsModal } from "@/pages/applications/pages/googleAdsIntegration/EditConversionSettingsModal";

type GoogleAdsAccount =
  InterludeTypes["GoogleAdsAccountWithConversionSettingsResponse"];

const cssSection = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 512px;
`;

const cssManagerAccountCard = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: ${theme.colors.staticBgContainer};
  border-radius: 8px;
`;

const cssManagerAccountInfo = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const cssGoogleAdsIcon = css`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const cssTableHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const cssActionButton = css`
  padding: 6px;
  border-radius: 4px;
  &:hover {
    background-color: ${theme.colors.staticBgContainer};
  }
`;

const cssActionIcon = css`
  font-size: 16px;
  color: ${theme.colors.staticFgBody};
`;

const cssActionIconDisabled = css`
  font-size: 16px;
  color: ${theme.colors.staticFgDisable};
`;

const cssEmptyRow = css`
  border: none;
`;

const cssEmpty = css`
  width: 80px;
  height: 64px;
`;

const cssEmptyContainer = css`
  padding: 24px 12px;
`;

const cssSkeletonCard = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: ${theme.colors.staticBgContainer};
  border-radius: 8px;
`;

const toCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

const getUniqueConversionCategories = (
  account: GoogleAdsAccount,
): Array<string> => {
  if (!account.conversionSettings || account.conversionSettings.length === 0) {
    return [];
  }
  // Only include categories from saved conversion settings (createdAt !== null)
  const categories = account.conversionSettings
    .filter((setting) => setting.createdAt !== null)
    .map((setting) => setting.conversionActionCategory)
    .filter(
      (category): category is string =>
        category !== null && category !== undefined,
    );
  return Array.from(new Set(categories));
};

export const ListGoogleAdsAccount: React.FC = function ListGoogleAdsAccount() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const dateFormat = useDateFormat({
    dateFormatOptions: {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    },
  });

  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const managerAccountQuery = interlude.googleAds.useGetManagerAccount({
    queries: {
      checkAccess: true,
    },
  });
  const accountsQuery = interlude.googleAds.useListManagerAccountAccounts();

  const hideConversionSettings = useMemo(() => {
    // Hide columns while loading or when hasEcModule is false to prevent flash
    return hasEcModuleQuery.data?.hasEcModule !== true;
  }, [hasEcModuleQuery.data]);

  const managerAccount = useMemo(() => {
    if (!managerAccountQuery.data) return null;
    return {
      name: managerAccountQuery.data.name,
      customerId: managerAccountQuery.data.googleAdsCustomerId,
      hasAccess: managerAccountQuery.data.hasAccess,
      isManager: managerAccountQuery.data.isManager,
    };
  }, [managerAccountQuery.data]);

  // A plain ad account can be linked here too, and its id looks exactly like an MCC id.
  // Labelling only this case keeps the common MCC connection unchanged.
  const isSingleAdAccount = managerAccount?.isManager === false;

  const accounts = useMemo(
    () => accountsQuery.data?.accounts ?? [],
    [accountsQuery.data],
  );

  const translateConversionCategory = useMemo(
    () => (category: string) => {
      const camelCaseCategory = toCamelCase(category);
      const translationKey = `applications.googleAds.conversionActionCategory.${camelCaseCategory}`;
      return t(translationKey);
    },
    [t],
  );

  const reloadManagerAccountAccountsMutation =
    interlude.googleAds.useReloadManagerAccountAccounts(undefined);

  const handleUpdate = () => {
    reloadManagerAccountAccountsMutation.mutate(undefined);
  };

  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
  const [deletingAccountId, setDeletingAccountId] = useState<number | null>(
    null,
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const editingAccount = useMemo(() => {
    if (editingAccountId === null) return null;
    return accounts.find((account) => account.id === editingAccountId) ?? null;
  }, [editingAccountId, accounts]);

  const deletingAccount = useMemo(() => {
    if (deletingAccountId === null) return null;
    return accounts.find((account) => account.id === deletingAccountId) ?? null;
  }, [deletingAccountId, accounts]);

  const handleEdit = useHandler((accountId: number) => {
    setEditingAccountId(accountId);
  });

  const handleCloseEditModal = useHandler(() => {
    setEditingAccountId(null);
  });

  const handleDelete = useHandler((accountId: number) => {
    setDeletingAccountId(accountId);
    setIsDeleteModalOpen(true);
  });

  const handleCloseDeleteModal = useHandler(() => {
    setIsDeleteModalOpen(false);
  });
  const handleResetDeletingAccountId = useHandler(() => {
    setDeletingAccountId(null);
  });
  const handleEditSuccess = useHandler(() => {
    handleCloseEditModal();
  });

  const deleteAccountMutation = interlude.googleAds.useDeleteAccount(
    {
      params: {
        googleAdsAccountId: deletingAccountId ?? 0,
      },
    },
    {
      onSuccess: () => {
        enqueueSnackbar(
          t("applications.googleAds.deleteAccountModal.deleteSuccess"),
          {
            variant: "success",
          },
        );
      },
      onError: () => {
        handleResetDeletingAccountId();
      },
    },
  );

  const handleConfirmDelete = useHandler(() => {
    if (deletingAccountId !== null) {
      deleteAccountMutation.mutate(undefined);
      handleCloseDeleteModal();
    }
  });

  const isManagerAccountLoading =
    managerAccountQuery.isPending || managerAccountQuery.isRefetching;
  const isAccountsLoading =
    accountsQuery.isPending || accountsQuery.isRefetching;

  return (
    <Stack gap="24px">
      {/* Google manager account section */}
      <Stack gap="8px" css={cssSection}>
        <Typography
          variant="h3"
          fontWeight="medium"
          color={theme.colors.staticFgTitle}
        >
          {t("applications.googleAds.managerAccount.title")}
        </Typography>
        <Typography variant="note" color={theme.colors.staticFgNote}>
          {t("applications.googleAds.managerAccount.description")}
        </Typography>
        {isManagerAccountLoading ? (
          <div css={cssSkeletonCard}>
            <Skeleton variant="circular" width={24} height={24} />
            <div css={cssManagerAccountInfo}>
              <Skeleton variant="text" width={150} height={20} />
              <Skeleton variant="text" width={100} height={16} />
            </div>
            <Skeleton variant="rounded" width={80} height={24} />
          </div>
        ) : managerAccount ? (
          <div css={cssManagerAccountCard}>
            <MotifIcon un-i-motif="google_ads_color" css={cssGoogleAdsIcon} />
            <div css={cssManagerAccountInfo}>
              <Typography
                variant="body"
                fontWeight={400}
                color={theme.colors.staticFgTitle}
              >
                {managerAccount.name}
              </Typography>
              <Typography variant="note" color={theme.colors.staticFgNote}>
                {managerAccount.customerId}
              </Typography>
            </div>
            {isSingleAdAccount ? (
              <Badge variant="plain">
                {t("applications.googleAds.managerAccount.singleAdAccount")}
              </Badge>
            ) : null}
            {managerAccount.hasAccess ? (
              <Badge variant="positive">
                {t("applications.googleAds.connected")}
              </Badge>
            ) : (
              <Badge variant="negative">
                {t("applications.googleAds.disconnected")}
              </Badge>
            )}
          </div>
        ) : null}
      </Stack>

      {/* Google ads account section */}
      <Stack gap="16px">
        <Stack gap="8px">
          <Typography
            variant="h3"
            fontWeight="medium"
            color={theme.colors.staticFgTitle}
          >
            {t("applications.googleAds.accounts.title")}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("applications.googleAds.accounts.description")}
          </Typography>
        </Stack>

        <div css={cssTableHeader}>
          <div />
          <Button variant="secondary" onClick={handleUpdate}>
            <Stack direction="row" gap="4px" alignItems="center">
              <MotifIcon un-i-motif="arrow_sync" />
              {t("applications.googleAds.accounts.update")}
            </Stack>
          </Button>
        </div>

        <TableContainer>
          <Table aria-label="google ads accounts table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography
                    variant="body"
                    fontWeight={700}
                    color={theme.colors.staticFgTitle}
                  >
                    {t("applications.googleAds.accounts.table.column.account")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body"
                    fontWeight={700}
                    color={theme.colors.staticFgTitle}
                  >
                    {t("applications.googleAds.accounts.table.column.status")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body"
                    fontWeight={700}
                    color={theme.colors.staticFgTitle}
                  >
                    {t(
                      "applications.googleAds.accounts.table.column.lastUpdated",
                    )}
                  </Typography>
                </TableCell>
                {!hideConversionSettings && (
                  <TableCell>
                    <Typography
                      variant="body"
                      fontWeight={700}
                      color={theme.colors.staticFgTitle}
                    >
                      {t(
                        "applications.googleAds.accounts.table.column.offlineConversion",
                      )}
                    </Typography>
                  </TableCell>
                )}
                <TableCell align={hideConversionSettings ? "center" : "right"}>
                  <Typography
                    variant="body"
                    fontWeight={700}
                    color={theme.colors.staticFgTitle}
                  >
                    {t("applications.googleAds.accounts.table.column.actions")}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isAccountsLoading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Stack direction="row" gap="8px" alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Stack gap="4px">
                          <Skeleton variant="text" width={120} height={20} />
                          <Skeleton variant="text" width={100} height={16} />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={80} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={140} height={20} />
                    </TableCell>
                    {!hideConversionSettings && (
                      <TableCell>
                        <Skeleton variant="rounded" width={60} height={24} />
                      </TableCell>
                    )}
                    <TableCell
                      align={hideConversionSettings ? "center" : "right"}
                    >
                      <Stack
                        direction="row"
                        gap="8px"
                        justifyContent={
                          hideConversionSettings ? "center" : "flex-end"
                        }
                      >
                        {!hideConversionSettings && (
                          <Skeleton variant="circular" width={28} height={28} />
                        )}
                        <Skeleton variant="circular" width={28} height={28} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : accounts.length === 0 ? (
                <TableRow css={cssEmptyRow}>
                  <TableCell
                    colSpan={hideConversionSettings ? 4 : 5}
                    align="center"
                  >
                    <Stack
                      gap="12px"
                      alignItems="center"
                      css={cssEmptyContainer}
                    >
                      <Empty css={cssEmpty} />
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgNote}
                      >
                        {t("applications.googleAds.accounts.table.empty")}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow key={account.id ?? account.googleAdsCustomerId}>
                    <TableCell>
                      <Stack direction="row" gap="8px" alignItems="center">
                        <MotifIcon
                          un-i-motif="google_ads_color"
                          css={cssGoogleAdsIcon}
                        />
                        <Stack>
                          <Typography
                            variant="body"
                            fontWeight={500}
                            color={theme.colors.staticFgBody}
                          >
                            {account.name}
                          </Typography>
                          <Typography
                            variant="body"
                            fontWeight={400}
                            color={theme.colors.staticFgNote}
                          >
                            {account.googleAdsCustomerId}
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {account.hasAccess ? (
                        <Badge variant="positive">
                          {t("applications.googleAds.connected")}
                        </Badge>
                      ) : (
                        <Badge variant="negative">
                          {t("applications.googleAds.disconnected")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgBody}
                      >
                        {dateFormat(account.updatedAt)}
                      </Typography>
                    </TableCell>
                    {!hideConversionSettings && (
                      <TableCell>
                        {(() => {
                          const uniqueCategories =
                            getUniqueConversionCategories(account);
                          if (uniqueCategories.length === 0) {
                            return (
                              <Typography
                                variant="body"
                                fontWeight={400}
                                color={theme.colors.staticFgBody}
                              >
                                –
                              </Typography>
                            );
                          }
                          return (
                            <Stack direction="row" gap="4px" flexWrap="wrap">
                              {uniqueCategories.map((category) => (
                                <Badge key={category} variant="plain">
                                  {translateConversionCategory(category)}
                                </Badge>
                              ))}
                            </Stack>
                          );
                        })()}
                      </TableCell>
                    )}
                    <TableCell
                      align={hideConversionSettings ? "center" : "right"}
                    >
                      <Stack
                        direction="row"
                        gap="8px"
                        justifyContent={
                          hideConversionSettings ? "center" : "flex-end"
                        }
                      >
                        {!hideConversionSettings &&
                          (() => {
                            const hasConversionSettings =
                              account.conversionSettings &&
                              account.conversionSettings.length > 0;
                            return (
                              <Tooltip
                                title={
                                  hasConversionSettings
                                    ? ""
                                    : t(
                                        "applications.googleAds.accounts.table.editDisabledTooltip",
                                      )
                                }
                                arrow
                                placement="top"
                              >
                                <span>
                                  <IconButton
                                    css={cssActionButton}
                                    onClick={() => {
                                      if (
                                        account.id !== null &&
                                        hasConversionSettings
                                      ) {
                                        handleEdit(account.id);
                                      }
                                    }}
                                    disabled={!hasConversionSettings}
                                    size="small"
                                  >
                                    <MotifIcon
                                      un-i-motif="edit"
                                      css={
                                        hasConversionSettings
                                          ? cssActionIcon
                                          : cssActionIconDisabled
                                      }
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}
                        {!isSingleAdAccount && (
                          <IconButton
                            css={cssActionButton}
                            onClick={() => {
                              if (account.id !== null) {
                                handleDelete(account.id);
                              }
                            }}
                            size="small"
                          >
                            <MotifIcon un-i-motif="bin" css={cssActionIcon} />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      {editingAccount && editingAccount.id !== null && (
        <EditConversionSettingsModal
          key={editingAccount.id}
          open={editingAccountId !== null}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          accountId={editingAccount.id}
          conversionSettings={editingAccount.conversionSettings}
        />
      )}
      {deletingAccount && (
        <DeleteAccountModal
          key={deletingAccount.id}
          open={isDeleteModalOpen}
          accountName={deletingAccount.name}
          isLoading={deleteAccountMutation.isPending}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Stack>
  );
};
